/**
 * The account editors: the display name, the data export and account
 * deletion. Sign-out is not here — `?edit=logout` is rendered by
 * `sections/sign-out-dialog.tsx`, which forgets the push device first.
 */
import { ApiError } from "@repo/client/api";
import type { QueryDef } from "@repo/client/data/core";
import { meOptions } from "@repo/client/data/me";
import { notificationPreferencesOptions } from "@repo/client/data/notifications";
import {
  equipmentOptions,
  injuriesOptions,
  measurementsOptions,
  parqOptions,
  profileOptions,
  trainingConfigOptions,
} from "@repo/client/data/profile";
import {
  type SetDisplayNameInput,
  setDisplayNameInput,
} from "@repo/contract/iam";
import {
  "profile-editors.delete_action" as deleteAction,
  "profile-editors.delete_body" as deleteBody,
  "profile-editors.delete_busy" as deleteBusy,
  "profile-editors.delete_confirm" as deleteConfirm,
  "profile-editors.delete_export_hint" as deleteExportHint,
  "profile-editors.delete_permanent" as deletePermanent,
  "profile-editors.delete_title" as deleteTitle,
  "profile-editors.done" as doneText,
  "profile-editors.export_action" as exportAction,
  "profile-editors.export_busy" as exportBusy,
  "profile-editors.export_done_body" as exportDoneBody,
  "profile-editors.export_done_title" as exportDoneTitle,
  "profile-editors.export_failed" as exportFailed,
  "profile-editors.export_format" as exportFormat,
  "profile-editors.export_helper" as exportHelper,
  "profile-editors.export_item_body" as exportItemBody,
  "profile-editors.export_item_health" as exportItemHealth,
  "profile-editors.export_item_prefs" as exportItemPrefs,
  "profile-editors.export_item_profile" as exportItemProfile,
  "profile-editors.export_title" as exportTitle,
  "profile-editors.eyebrow_account" as eyebrowAccount,
  "profile-editors.name_error" as nameError,
  "profile-editors.name_title" as nameTitle,
} from "@repo/i18n";
import { useQueryClient } from "@tanstack/react-query";
import { Check, CircleCheck, Download } from "lucide-react";
import { useCallback, useId, useState } from "react";
import { useApi } from "../../../data/api";
import { fromQueryDef } from "../../../data/query-client";
import { share } from "../../../native";
import { Button, Icon } from "../../../ui/controls";
import { AlertDialog } from "../../../ui/overlay";
import {
  type EditorHost,
  EditorShell,
  useSaveFlow,
  useSectionRead,
} from "./editor-shell";
import { NameForm } from "./name-form";
import { FieldError, Helper, InfoNote } from "./parts";
import { MEASUREMENT_KINDS } from "./payloads";
import { useEditorForm } from "./use-editor-form";
import {
  useDeleteAccountMutation,
  useDisplayNameMutation,
} from "./use-editor-mutation";

interface EditorProps {
  readonly host: EditorHost;
}

// ── name ───────────────────────────────────────────────────────────────────

/** `PUT /auth/me/display-name`. Blank clears it (`null`, D40). */
export function NameEditor({ host }: EditorProps) {
  const api = useApi();
  const read = useSectionRead(meOptions(api));
  const flow = useSaveFlow(useDisplayNameMutation(), host);
  const stored = read.data?.displayName ?? null;
  const form = useEditorForm({
    onSubmit: (payload: SetDisplayNameInput) => flow.save(payload),
    schema: setDisplayNameInput,
    server: stored,
    toDraft: (name: string | null) => name ?? "",
    toPayload: (name: string) => ({
      displayName: name.trim() === "" ? null : name.trim(),
    }),
  });
  return (
    <EditorShell
      disabled={form.draft.trim() === (stored ?? "")}
      eyebrow={eyebrowAccount()}
      failure={flow.failure}
      host={host}
      loadFailed={read.loadFailed}
      loading={read.loading}
      onSave={form.submit}
      pending={flow.pending}
      title={nameTitle()}
    >
      <NameForm
        error={Object.keys(form.errors).length > 0 ? nameError() : undefined}
        onChange={form.setDraft}
        value={form.draft}
      />
    </EditorShell>
  );
}

// ── export ─────────────────────────────────────────────────────────────────

type ExportState = "busy" | "done" | "failed" | "idle";

/** The web fallback when there is no share sheet: a file download. */
function download(json: string, filename: string): void {
  const url = URL.createObjectURL(
    new Blob([json], { type: "application/json" })
  );
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * A JSON of what the app holds about the person, built on the device from
 * their own reads — there is no export endpoint. Handed to the share sheet
 * (`share()` from `src/native`), or downloaded on a web browser that has
 * none. A section never written (`404`) is `null` in the file.
 */
export function ExportEditor({ host }: EditorProps) {
  const api = useApi();
  const client = useQueryClient();
  const [state, setState] = useState<ExportState>("idle");

  const build = useCallback(async () => {
    const read = async <T,>(def: QueryDef<T>): Promise<T | null> => {
      try {
        return await client.fetchQuery(fromQueryDef(def));
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) {
          return null;
        }
        throw error;
      }
    };
    const [
      account,
      profile,
      trainingConfig,
      equipment,
      injuries,
      parq,
      weight,
      bodyFat,
      notifications,
    ] = await Promise.all([
      read(meOptions(api)),
      read(profileOptions(api)),
      read(trainingConfigOptions(api)),
      read(equipmentOptions(api)),
      read(injuriesOptions(api)),
      read(parqOptions(api)),
      read(measurementsOptions(api, { kind: MEASUREMENT_KINDS.weight.kind })),
      read(measurementsOptions(api, { kind: MEASUREMENT_KINDS.bodyFat.kind })),
      read(notificationPreferencesOptions(api)),
    ]);
    const exportedAt = new Date().toISOString();
    return {
      filename: `mypocketgym-${exportedAt.slice(0, 10)}.json`,
      json: JSON.stringify(
        {
          account,
          equipment,
          exportedAt,
          injuries,
          measurements: { bodyFat, weight },
          notificationPreferences: notifications,
          parq,
          profile,
          trainingConfig,
        },
        null,
        2
      ),
    };
  }, [api, client]);

  const run = useCallback(async () => {
    setState("busy");
    try {
      const { json, filename } = await build();
      if (!(await share({ text: json, title: exportTitle() }))) {
        download(json, filename);
      }
      setState("done");
    } catch {
      setState("failed");
    }
  }, [build]);

  const start = useCallback(() => {
    run().catch(() => setState("failed"));
  }, [run]);

  if (state === "done") {
    return (
      <EditorShell
        eyebrow={eyebrowAccount()}
        footer={
          <Button className="w-full" onClick={host.onClose} size="lg">
            {doneText()}
          </Button>
        }
        host={host}
        title={exportDoneTitle()}
      >
        <div className="flex flex-col items-center py-4 text-center">
          <span className="grid size-16 place-items-center rounded-full bg-success-soft text-success-ink">
            <Icon icon={CircleCheck} size={24} />
          </span>
          <p className="mt-4 max-w-[36ch] text-base text-ink-2 leading-relaxed">
            {exportDoneBody()}
          </p>
        </div>
      </EditorShell>
    );
  }

  const items = [
    exportItemProfile(),
    exportItemBody(),
    exportItemHealth(),
    exportItemPrefs(),
  ];
  return (
    <EditorShell
      eyebrow={eyebrowAccount()}
      failure={state === "failed" ? exportFailed() : null}
      host={host}
      onSave={start}
      pending={state === "busy"}
      saveLabel={state === "busy" ? exportBusy() : exportAction()}
      title={exportTitle()}
    >
      <Helper>{exportHelper()}</Helper>
      <ul className="overflow-hidden rounded-2xl bg-surface shadow">
        {items.map((item, i) => (
          <li
            className={
              i > 0
                ? "flex items-center gap-3 border-line border-t px-4 py-3.5 text-base"
                : "flex items-center gap-3 px-4 py-3.5 text-base"
            }
            key={item}
          >
            <span className="shrink-0 text-success-ink">
              <Icon bold icon={Check} size={16} />
            </span>
            {item}
          </li>
        ))}
      </ul>
      <p className="mt-4 flex gap-2 text-ink-2 text-sm leading-snug">
        <span className="mt-px shrink-0 text-ink-3">
          <Icon icon={Download} size={16} />
        </span>
        {exportFormat()}
      </p>
    </EditorShell>
  );
}

// ── delete ─────────────────────────────────────────────────────────────────

/**
 * `DELETE /auth/account`, behind A2's checkbox gate (editors.tsx:1178-1228),
 * in T14's AlertDialog. Its confirm does not close the dialog: on success
 * the session is discarded — never `signOut()`, which would present the
 * revoked credential — and the app returns to sign-in; on a refusal (the
 * person owns a populated group, `409`) the reason is shown here.
 */
export function DeleteEditor({ host }: EditorProps) {
  const api = useApi();
  const mutation = useDeleteAccountMutation();
  const [sure, setSure] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const checkboxId = useId();
  const { describeError, onClose } = host;
  const { mutate } = mutation;

  const openChange = useCallback(
    (next: boolean) => {
      if (!next) {
        onClose();
      }
    },
    [onClose]
  );
  const toggle = useCallback(() => setSure((was) => !was), []);
  const confirm = useCallback(() => {
    if (!sure) {
      return;
    }
    setFailure(null);
    mutate(undefined, {
      onError: (error) => setFailure(describeError(error)),
      onSuccess: () => {
        api.discardSession().catch(() => undefined);
      },
    });
  }, [api, sure, mutate, describeError]);

  return (
    <AlertDialog
      confirmLabel={mutation.isPending ? deleteBusy() : deleteAction()}
      onConfirm={confirm}
      onOpenChange={openChange}
      open={host.open}
      pending={!sure || mutation.isPending}
      title={deleteTitle()}
      tone="destructive"
    >
      <div className="rounded-2xl bg-danger-soft p-4">
        <p className="font-semibold text-base text-danger">
          {deletePermanent()}
        </p>
        <p className="mt-1 text-base text-ink leading-relaxed">
          {deleteBody()}
        </p>
      </div>
      <label
        className="mt-4 flex min-h-12 w-full cursor-pointer items-center gap-3 rounded-lg bg-surface p-4 text-left ring-1 ring-line"
        htmlFor={checkboxId}
      >
        <input
          checked={sure}
          className="peer sr-only"
          id={checkboxId}
          onChange={toggle}
          type="checkbox"
        />
        <span
          className={
            sure
              ? "grid size-6 shrink-0 place-items-center rounded-sm border-2 border-danger bg-danger text-on-danger peer-focus-visible:ring-2 peer-focus-visible:ring-accent"
              : "grid size-6 shrink-0 place-items-center rounded-sm border-2 border-line-strong peer-focus-visible:ring-2 peer-focus-visible:ring-accent"
          }
        >
          {sure ? <Icon bold icon={Check} size={16} /> : null}
        </span>
        <span className="font-medium text-base leading-snug">
          {deleteConfirm()}
        </span>
      </label>
      {failure ? <FieldError>{failure}</FieldError> : null}
      <InfoNote>{deleteExportHint()}</InfoNote>
    </AlertDialog>
  );
}
